import React from "react"
import type { ReactNode } from "react"

const getIsEnded = (
  currentPage: number,
  pageLimit: number,
  isLoading: boolean,
  error: unknown
) => pageLimit && pageLimit === currentPage && !isLoading && !error

const isEmpty = (
  items: unknown[],
  isLoading: boolean,
  error: unknown
) => !items.length && !isLoading && !error

export type InfiniteScrollProps<T = unknown> = {
  /** Element type to render as the wrapper */
  as?: React.ElementType
  /** Content shown while loading */
  loader?: ReactNode
  /** Content shown when all pages have been loaded */
  completionMessage?: ReactNode
  /** Content shown when there are no results */
  emptyMessage?: ReactNode
  /** Content shown when an error occurs */
  errorMessage?: ReactNode
  /** Function that loads a page of items. Receives the page number. */
  loadFunction: (page: number) => Promise<T[]>
  /** Render function called for each item */
  children: (item: T, index: number) => React.JSX.Element
  /** Callback when loadFunction rejects */
  onError?: (error: unknown) => void
  /** Distance in pixels from the bottom to trigger loading */
  threshold?: number
  /** Maximum number of pages to load */
  pageLimit?: number
  /** CSS selector for the scrollable parent element */
  scrollParentSelector?: string
  /** CSS selector for the scroll content element */
  scrollContentSelector?: string
}

/**
 * Loads and displays paginated data as the user scrolls.
 * Monitors scroll position and automatically fetches the next page when the threshold is reached.
 */
const InfiniteScroll = <T,>({
  as: Component = "div",
  loader = <div>Loading...</div>,
  completionMessage = null,
  emptyMessage = null,
  errorMessage = null,
  loadFunction,
  onError,
  children: mapCallback,
  threshold = 500,
  pageLimit = 1000,
  scrollParentSelector,
  scrollContentSelector
}: InfiniteScrollProps<T>) => {
  const [page, setPage] = React.useState(1)
  const [items, setItems] = React.useState<T[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState<unknown>(null)
  const position = React.useRef(0)
  const thresholdReached = React.useRef(false)
  const abortRequest = React.useRef(false)
  const isEnded = getIsEnded(page, pageLimit, isLoading, error)

  const scrollCb = React.useCallback(
    () => {
      const scrollArea = scrollParentSelector
        ? document.querySelector(scrollParentSelector)
        : document.scrollingElement
      const scrollContent = scrollContentSelector
        ? document.querySelector(scrollContentSelector)
        : scrollArea

      if (!scrollArea || !(scrollContent instanceof HTMLElement)) return

      const scrollContentHeight = scrollContent.offsetHeight
      const scrollPosition = scrollArea.scrollTop
      const windowHeight = window.innerHeight
      const scrollDifference = scrollContentHeight - threshold - scrollPosition
      if (scrollDifference <= windowHeight && !thresholdReached.current) {
        thresholdReached.current = true
        // if scrolling down
        if (position.current < scrollPosition) {
          // Increase page number if below limit
          setPage(currentPage =>
            pageLimit && pageLimit > currentPage ? currentPage + 1 : currentPage
          )
        }
      }
      if (scrollDifference + threshold === windowHeight) {
        // End reached
        thresholdReached.current = false
      }
      position.current = scrollPosition
    },
    [threshold, pageLimit, scrollParentSelector, scrollContentSelector]
  )

  // Page changes Effect
  React.useEffect(() => {
    // This should only load if page limit is not reached
    // and if it should not load for the initial page as
    // the loadfunction effect handles that.
    if (pageLimit && pageLimit >= page && page !== 1) {
      abortRequest.current = false
      setIsLoading(true)
      loadFunction(page)
        .then(newItems => {
          thresholdReached.current = false

          if (!abortRequest.current) {
            setItems(currentItems => [...currentItems, ...newItems])
            setIsLoading(false)
          }
        })
        .catch(err => {
          if (!abortRequest.current) {
            setError(err)
            setIsLoading(false)
            if (onError) {
              onError(err)
            }
          }
        })
    }
    return () => {
      abortRequest.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageLimit, abortRequest])

  // LoadFunction's dependency effect
  React.useEffect(() => {
    abortRequest.current = false
    setIsLoading(true)
    loadFunction(page)
      .then(newItems => {
        if (!abortRequest.current) {
          setItems(newItems)
          setIsLoading(false)
        }
      })
      .catch(err => {
        if (!abortRequest.current) {
          setError(err)
          setIsLoading(false)
          if (onError) {
            onError(err)
          }
        }
      })
    return () => {
      abortRequest.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadFunction])

  // Scroll Effect
  React.useEffect(() => {
    const scroller = scrollParentSelector
      ? document.querySelector(scrollParentSelector)
      : window
    if (scroller) {
      scroller.addEventListener("scroll", scrollCb)
    }
    return () => {
      if (scroller) {
        scroller.removeEventListener("scroll", scrollCb)
      }
    }
  }, [scrollCb, scrollParentSelector])

  return (
    <>
      <Component aria-live="polite" role="region">
        {items.map(mapCallback)}
      </Component>
      {isLoading && loader}
      {isEmpty(items, isLoading, error) && emptyMessage}
      {isEnded && completionMessage}
      {!!error && errorMessage}
    </>
  )
}

export { InfiniteScroll }
export default InfiniteScroll
